/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import Autocomplete from "@mui/material/Autocomplete";

import {
    InventoryItem,
    Nullable,
    StorageLocation,
    StorageLocationDetailed,
    TechwiseOffice,
    TechwiseOfficeDetailed,
} from "../../utils/Interfaces";

import { selectExistingTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesSlice";
import { techwiseOfficeDetails } from "../materials-techwise-office/techwiseOfficeFunctions";
import { updateTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesFunctions";
import { NumericFormat } from "react-number-format";
import { ManageAccounts } from "@mui/icons-material";
import { inventoryAdmin } from "./materialTransferFunctions";
import { storageLocationDetails } from "../materials-storage-location/storageLocationFunctions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ModelName } from "../../utils/Requests";
import DeferredAttachmentManager from "../../components/general/DeferredAttachmentManager";
import AnimatedCompletionCheck from "../../components/confirmation/AnimatedCompletionCheck";
import { delayedAction } from "../../utils/Functions";

const InventoryAdmin = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [selectedTechwiseOffice, setSelectedTechwiseOffice] = useState<Nullable<TechwiseOffice>>(null);

    const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);
    const [selectedStorageLocation, setSelectedStorageLocation] = useState<Nullable<StorageLocation>>(null);

    const techwiseOffices: TechwiseOffice[] = useAppSelector(selectExistingTechwiseOffices);

    const [inventories, setInventories] = useState<InventoryItem[]>([]);
    const [selectedInventory, setSelectedInventory] = useState<Nullable<InventoryItem>>(null);

    const [officesLoaded, setOfficesLoaded] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(true);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [complete, setComplete] = useState<boolean>(false);

    const [amount, setAmount] = useState<number>(0);
    const [targetAmount, setTargetAmount] = useState<number>(0);
    const [transferNotes, setTransferNotes] = useState<string>("");

    const [inventoryTransferId, setInventoryTransferId] = useState<Nullable<number>>(null);

    function getFormErrors(): string {
        if (techwiseOffices.length === 0) {
            return "No Techwise offices available.";
        }

        if (storageLocations.length === 0) {
            return "The selected Techwise office has no storage locations available.";
        }

        if (selectedInventory === null) {
            return "Please select an inventory.";
        }

        if (selectedInventory.amount === amount && selectedInventory.target === targetAmount) {
            return "No changes detected.";
        }

        return "";
    }

    function getFormWarnings(): string {
        // Don't display warnings if errors exist.
        if (getFormErrors() !== "") {
            return "";
        }

        if (amount !== selectedInventory?.amount) {
            return "Changing inventory amounts from the admin page will results in loss of inventory traceability.";
        }

        return "";
    }

    function setSelectedInventoryState(inventory: Nullable<InventoryItem>) {
        setSelectedInventory(inventory);
        if (inventory === null) {
            setAmount(0);
            setTargetAmount(0);
        } else {
            setAmount(inventory.amount);
            setTargetAmount(inventory.target);
        }
    }

    // Logic to disable the complete check after a set period of time.
    useEffect(() => {
        if (complete) {
            delayedAction(2000, () => setComplete(false));
        }
    }, [complete]);

    useEffect(() => {
        setLoading(true);
        if (techwiseOffices.length > 0) {
            setSelectedTechwiseOffice(techwiseOffices[0]);
        } else {
            // If the count of offices is 0, attempt a single reload. Use a Boolean flag to prevent recursive requests.
            if (!officesLoaded) {
                setOfficesLoaded(true);
                updateTechwiseOffices(dispatch, () => {});
            }
        }
    }, [techwiseOffices]);

    useEffect(() => {
        setSelectedStorageLocation(null);
        setInventories([]);
        setSelectedInventoryState(null);
        if (selectedTechwiseOffice !== null) {
            techwiseOfficeDetails(selectedTechwiseOffice?.id, dispatch, (techwiseOfficeDetailed: TechwiseOfficeDetailed) => {
                setStorageLocations(techwiseOfficeDetailed.locations);
                if (techwiseOfficeDetailed.locations.length > 0) {
                    setSelectedStorageLocation(techwiseOfficeDetailed.locations[0]);
                }
                setLoading(false);
            });
        }
    }, [selectedTechwiseOffice]);

    useEffect(() => {
        setSelectedInventory(null);
        if (selectedStorageLocation !== null) {
            storageLocationDetails(selectedStorageLocation?.id, dispatch, (storageLocationDetailed: StorageLocationDetailed) => {
                let sortedInventories: InventoryItem[] = storageLocationDetailed.inventory.sort(
                    (a: InventoryItem, b: InventoryItem) => {
                        let aPrefix: string = a.amount === 0 ? "1" : "0";
                        let bPrefix: string = b.amount === 0 ? "1" : "0";

                        let aStr: string = `${aPrefix}${a.material.name}`;
                        let bStr: string = `${bPrefix}${b.material.name}`;

                        return aStr.localeCompare(bStr);
                    }
                );
                setInventories(sortedInventories);
                if (storageLocationDetailed.inventory.length > 0) {
                    setSelectedInventoryState(sortedInventories[0]);
                } else {
                    setSelectedInventoryState(null);
                }
            });
        }
    }, [selectedStorageLocation]);

    if (loading) {
        return (
            <Box sx={{ mt: 5 }}>
                <Stack direction="row" justifyContent="center" sx={{ mt: 15, mb: 10 }}>
                    <CircularProgress size={40} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            {getFormErrors() === "" && getFormWarnings() === "" && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    All entries are valid.
                </Alert>
            )}
            {getFormWarnings() !== "" && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {getFormWarnings()}
                </Alert>
            )}
            {getFormErrors() !== "" && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {getFormErrors()}
                </Alert>
            )}
            <Grid spacing={3} sx={{ mt: 3 }} container>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedTechwiseOffice(value ? value : techwiseOffices.length > 0 ? techwiseOffices[0] : null);
                        }}
                        id="techwiseOffices"
                        getOptionKey={(option: TechwiseOffice) => option.id}
                        options={techwiseOffices}
                        value={selectedTechwiseOffice}
                        getOptionLabel={(option: TechwiseOffice) => option.name}
                        renderInput={(params) => <TextField required {...params} label="Techwise Office" />}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        onChange={(_event, value) => {
                            setSelectedStorageLocation(value ? value : storageLocations.length > 0 ? storageLocations[0] : null);
                        }}
                        id="storageLocations"
                        getOptionKey={(option: StorageLocation) => option.id}
                        options={storageLocations}
                        value={selectedStorageLocation}
                        getOptionLabel={(option: StorageLocation) => option.name}
                        renderInput={(params) => <TextField required {...params} label="Storage Locations" />}
                    />
                </Grid>
            </Grid>

            <Grid spacing={3} sx={{ mt: 3 }} container>
                <Grid item xs={6}>
                    <Autocomplete
                        disablePortal
                        disabled={inventories.length === 0}
                        onChange={(_event, value) => {
                            setSelectedInventoryState(value ? value : inventories.length > 0 ? inventories[0] : null);
                        }}
                        id="inventories"
                        getOptionKey={(option: InventoryItem) => option.id}
                        options={inventories}
                        value={selectedInventory}
                        getOptionLabel={(option: InventoryItem) =>
                            `${option.material.name} (${option.material.part_number}) | (${option.amount})`
                        }
                        renderInput={(params) => (
                            <TextField
                                required
                                {...params}
                                label="Inventory"
                                helperText="Material Name (Part #) | (Amt. Available)"
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Grid container>
                        <Grid xs={6} sx={{ pr: 1.5 }} item>
                            <NumericFormat
                                disabled={selectedInventory === null}
                                value={amount}
                                customInput={TextField}
                                style={{ marginLeft: 0, marginTop: 0, width: "100%" }}
                                label="Amount"
                                thousandSeparator=","
                                decimalScale={0}
                                allowNegative={false}
                                allowLeadingZeros={false}
                                onValueChange={(values) => {
                                    setAmount(parseInt(values.value));
                                }}
                            />
                        </Grid>
                        <Grid xs={6} sx={{ pl: 1.5 }} item>
                            <NumericFormat
                                disabled={selectedInventory === null}
                                value={targetAmount}
                                customInput={TextField}
                                style={{ marginLeft: 0, marginTop: 0, width: "100%" }}
                                label="Target Amount"
                                thousandSeparator=","
                                decimalScale={0}
                                allowNegative={false}
                                allowLeadingZeros={false}
                                onValueChange={(values) => {
                                    setTargetAmount(parseInt(values.value));
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        disabled={selectedInventory !== null && selectedInventory.amount === amount}
                        fullWidth
                        multiline
                        rows={4}
                        sx={{ mt: 3 }}
                        value={transferNotes}
                        onChange={(event) => setTransferNotes(event.target.value)}
                        label="Transfer Notes"
                        name="transfer_notes"
                    />
                </Grid>
                <Grid item xs={6}>
                    <DeferredAttachmentManager
                        model={ModelName.InventoryTransfer}
                        idKey={inventoryTransferId}
                        editing={true}
                        readOnly={selectedInventory !== null && selectedInventory.amount === amount}
                        onFilesUploaded={() => {
                            setComplete(true);
                            setSelectedStorageLocation({ ...selectedStorageLocation } as StorageLocation);
                            setTransferNotes("");
                            setSubmitting(false);
                            setInventoryTransferId(null);
                        }}
                    />
                </Grid>
            </Grid>

            {submitting && <CircularProgress size={16} sx={{ mt: 6, ml: 2, mb: 0.8 }} />}

            {!submitting && (
                <Stack direction="row" justifyContent="flex-start" sx={{ mt: 5 }}>
                    <Button
                        color={selectedInventory !== null && selectedInventory.amount !== amount ? "warning" : "primary"}
                        variant="contained"
                        disabled={getFormErrors() !== "" || submitting}
                        onClick={() => {
                            setSubmitting(true);
                            inventoryAdmin(
                                selectedInventory as InventoryItem,
                                amount,
                                targetAmount,
                                transferNotes,
                                dispatch,
                                (createdTransferId: Nullable<number>) => {
                                    // If this is only a target amount change, stop the loading wheel here
                                    // since the deferred attachment manager won't provide an update.
                                    if (selectedInventory?.amount === amount) {
                                        // Modify the selected storage location, this will re-run the useEffect and update the values.
                                        setSelectedStorageLocation({ ...(selectedStorageLocation as StorageLocation) });
                                        setSubmitting(false);
                                        setComplete(true);
                                    }
                                    setInventoryTransferId(createdTransferId);
                                },
                                () => setSubmitting(false)
                            );
                        }}
                    >
                        <ManageAccounts />
                        <Typography sx={{ ml: 1 }}>
                            {selectedInventory !== null && selectedInventory.amount !== amount
                                ? "Admin Update Amount"
                                : "Admin Update Target Amount"}
                        </Typography>
                    </Button>
                    {complete && (
                        <Stack direction="row" justifyContent="flex-start" sx={{ mt: 1, ml: 3 }}>
                            <AnimatedCompletionCheck size={24} />
                            <Typography color="success.main" sx={{ ml: 2, mt: -0.35 }} variant="h6">
                                Admin Operation Successful
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            )}
        </Box>
    );
};

export default InventoryAdmin;
